import { getApps } from "firebase/app";
import { getInstallations, getId } from "firebase/installations";
import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { getDb } from "./firebase";
import { notifyBugsnag } from "./bugsnag";

import pkg from "../package.json";

// Minimal shape for navigator.userAgentData — not in the default DOM lib.
type UADBrand = { brand: string; version: string };
type UserAgentData = {
  brands: UADBrand[];
  platform: string;
  getHighEntropyValues?: (hints: string[]) => Promise<{
    platform?: string;
    platformVersion?: string;
    model?: string;
  }>;
};

// Guard against the auth listener firing twice for the same uid within one
// page load (StrictMode double-invoke, fresh-token refresh, etc.).
const recordedUids = new Set<string>();

export function recordLoginInfo(uid: string): void {
  if (recordedUids.has(uid)) return;
  recordedUids.add(uid);
  // Fire-and-forget — best-effort telemetry must never block UI or surface
  // an error to the user.
  void writeLoginInfo(uid).catch(notifyBugsnag);
}

// Exposed for tests; reset the in-memory dedupe set.
export function _resetRecordedForTests(): void {
  recordedUids.clear();
}

async function writeLoginInfo(uid: string): Promise<void> {
  const app = getApps()[0];
  if (!app) return;

  const fid = await getId(getInstallations(app));
  const { osName, osVersion, deviceName } = await describeClient();
  const ref = doc(getDb(), "userData", uid, "loginInfo", fid);
  const snap = await getDoc(ref);

  const payload: Record<string, unknown> = {
    platform: "web",
    versionName: pkg.version,
    osName,
    osVersion,
    deviceName,
    locale: navigator.language || "en-US",
    lastLoginAt: serverTimestamp(),
  };
  if (!snap.exists()) {
    payload.firstLoginAt = serverTimestamp();
  }
  await setDoc(ref, payload, { merge: true });
}

async function describeClient(): Promise<{
  osName: string;
  osVersion: string;
  deviceName: string;
}> {
  const uad = (navigator as Navigator & { userAgentData?: UserAgentData })
    .userAgentData;
  if (uad?.getHighEntropyValues) {
    try {
      const hints = await uad.getHighEntropyValues([
        "platform",
        "platformVersion",
      ]);
      const brand =
        uad.brands.find((b) => !/Not.A.Brand/i.test(b.brand))?.brand ??
        "Browser";
      const brandVersion =
        uad.brands.find((b) => b.brand === brand)?.version ?? "";
      return {
        osName: hints.platform || uad.platform || "Unknown",
        osVersion: hints.platformVersion || "",
        deviceName: brandVersion ? `${brand} ${brandVersion}` : brand,
      };
    } catch {
      // fall through to UA-string parsing
    }
  }
  return parseUserAgent(navigator.userAgent ?? "");
}

function parseUserAgent(ua: string): {
  osName: string;
  osVersion: string;
  deviceName: string;
} {
  let osName = "Unknown";
  let osVersion = "";

  const ios = ua.match(/(?:iPhone|iPad|iPod) OS ([\d_]+)/);
  const android = ua.match(/Android ([\d.]+)/);
  const win = ua.match(/Windows NT ([\d.]+)/);
  const mac = ua.match(/Mac OS X ([\d_.]+)/);

  if (ios) {
    osName = "iOS";
    osVersion = ios[1].replace(/_/g, ".");
  } else if (android) {
    osName = "Android";
    osVersion = android[1];
  } else if (win) {
    osName = "Windows";
    osVersion = win[1];
  } else if (mac) {
    osName = "macOS";
    osVersion = mac[1].replace(/_/g, ".");
  } else if (/Linux/.test(ua)) {
    osName = "Linux";
  }

  let browser = "Browser";
  let browserVersion = "";
  const edge = ua.match(/Edg\/([\d.]+)/);
  const firefox = ua.match(/Firefox\/([\d.]+)/);
  const chrome = ua.match(/Chrome\/([\d.]+)/);
  const safari = ua.match(/Version\/([\d.]+).*Safari/);
  if (edge) {
    browser = "Edge";
    browserVersion = edge[1].split(".")[0];
  } else if (firefox) {
    browser = "Firefox";
    browserVersion = firefox[1].split(".")[0];
  } else if (chrome) {
    // Chrome must be checked after Edge — Edge UA also contains "Chrome/".
    browser = "Chrome";
    browserVersion = chrome[1].split(".")[0];
  } else if (safari) {
    browser = "Safari";
    browserVersion = safari[1].split(".")[0];
  }

  return {
    osName,
    osVersion,
    deviceName: browserVersion ? `${browser} ${browserVersion}` : browser,
  };
}
